import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import emooleadersImage from '../images/emooleaders-1.png';

const PageContainer = styled(motion.div)`
  padding: 4rem 2rem;
  background-color: #f0f4f8;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
`;

const ContentWrapper = styled(motion.div)`
  max-width: 1200px;
  width: 100%;
  background-color: #ffffff;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(28, 47, 79, 0.1);
  padding: 3rem;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const Title = styled(motion.h1)`
  color: #1c2f4f;
  margin-bottom: 2rem;
  text-align: center;
  font-size: 2.5rem;
  position: relative;

  &:after {
    content: '';
    display: block;
    width: 100px;
    height: 4px;
    background-color: #1c2f4f;
    margin: 1rem auto 0;
    border-radius: 2px;
  }
`;

const ContentContainer = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 3rem;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
  }
`;

const TextContainer = styled(motion.div)`
  flex: 1;
`;

const MissionTitle = styled(motion.h2)`
  color: #1c2f4f;
  margin-bottom: 0.5rem;
  font-size: 1.8rem;
`;

const Slogan = styled(motion.p)`
  color: #1c2f4f;
  font-style: italic;
  font-size: 1.3rem;
  margin-bottom: 1rem;
`;

const MissionText = styled(motion.p)`
  color: #1c2f4f;
  line-height: 1.8;
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
`;

const NumberedList = styled(motion.ol)`
  color: #1c2f4f;
  line-height: 1.8;
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
`;

const ListItem = styled(motion.li)`
  margin-bottom: 0.5rem;
`;

const ImageContainer = styled(motion.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 500px;
  width: 100%;
`;

const FoundersImage = styled(motion.img)`
  width: 100%;
  height: auto;
  border-radius: 12px;
  border: 3px solid #e6eef8;
  box-shadow: 0 8px 16px rgba(28, 47, 79, 0.1);
`;

const ImageCaption = styled(motion.h3)`
  color: #1c2f4f;
  margin-top: 1rem;
  font-size: 1.2rem;
  text-align: center;
`;

const Button = styled(motion.button)`
  background-color: #1c2f4f;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: bold;
  transition: background-color 0.3s ease;
  box-shadow: 0 4px 6px rgba(28, 47, 79, 0.1);

  &:hover {
    background-color: #2c3e50;
  }
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background-color: white;
  padding: 2rem;
  border-radius: 10px;
  max-width: 500px;
  width: 90%;
`;

const CloseButton = styled(motion.button)`
  background-color: #1c2f4f;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1rem;
  margin-top: 1rem;
`;

const WhoAreWe = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  const handleContactClick = () => {
    setIsModalOpen(true);
  };

  return (
    <PageContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <ContentWrapper
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Title variants={itemVariants}>Who Are We</Title>
        <ContentContainer>
  <TextContainer variants={containerVariants}>
    <MissionTitle variants={itemVariants}>What We Do</MissionTitle>
    <MissionText variants={itemVariants}>
      We're a group of Bay Area students and teachers offering free math tutoring to middle and high school students in Oakland. We help with:
    </MissionText>
    <NumberedList variants={containerVariants}>
      <ListItem variants={itemVariants}>Homework support and test prep</ListItem>
      <ListItem variants={itemVariants}>One-on-one tutoring in algebra, geometry, and calculus</ListItem>
      <ListItem variants={itemVariants}>Flexible scheduling to work around your commitments</ListItem>
    </NumberedList>
    <MissionText variants={itemVariants}>
      Math shouldn't be a barrier to anyone's education. If you're stuck on a math problem or trying to understand a new concept, we're here to help. No catch, no cost.
    </MissionText>
            <Button
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleContactClick}
            >
              Contact Us
            </Button>
          </TextContainer>
          <ImageContainer variants={containerVariants}>
          <FoundersImage
            src={emooleadersImage}
            alt="Founders of EMOO"
            variants={itemVariants}
          />
            <ImageCaption variants={itemVariants}>Meet Our Founders</ImageCaption>
          </ImageContainer>
        </ContentContainer>
      </ContentWrapper>

      {isModalOpen && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <ModalContent
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
          >
            <h2>Contact Us</h2>
            <p>Email us anytime at equalmathopportunitiesoakland@gmail.com</p>
            <CloseButton onClick={() => setIsModalOpen(false)}>Close</CloseButton>
          </ModalContent>
        </Modal>
      )}
    </PageContainer>
  );
};

export default WhoAreWe;